import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABProducerSelect } from '../lib/ab/select-admin';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { CEventAdminView, cEventsAdminViewByProducer, cEventsProducerView, CSessionEventView, cSessionsGetManaged } from '../lib/mdl/c-event-admin';
import { c_producers } from '../lib/mdl/c-producer';
import { c_venues } from '../lib/mdl/c-venue';
import { i_orders, IOrder, iOrderLink } from '../lib/mdl/i-order';
import { i_events, i_providers, IEvent, IEventAdminView, iEventGetNextOrFirstTime, iEventLink, IProvider } from '../lib/mdl/i-provider';
import { uiSetBG } from '../lib/ui/bg';
import { makeActionButton, UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { yGetI18N } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $time } from '../lib/y/time';
import { YId } from '../lib/y/types';

class LinkEventDialog extends UIDialog {
    constructor(imported: IEvent, events: CEventAdminView[]) {
        super({ close: true, classes: 'm-500' });

        const event_values = events.map(e => ({ key: e.id, name: yGetI18N(e.name) })).sort((a, b) => $fn.ICOMPARE(a.name, b.name));

        const event = new UISelect(event_values, { searchable: true });

        for(const v of event_values) {
            if(v.name.toLocaleLowerCase() == yGetI18N(imported.name).toLocaleLowerCase())
                event.value = v.key;
        }

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Link event'),
            $el.H3('heading', yGetI18N(imported.name)),
            new UILabeled(event, { above: 'Database event' }),
            $el.DIV('l-button-row', [
                new UIButton({ title: "Cancel", classes: 'm-round m-compact', on_click: () => { this.dismiss(null) } }),
                new UIButton({
                    title: "Link", classes: 'm-round m-compact m-hollow', on_click: () => {
                        const event_id = event.value;
                        if(!event_id) return;

                        return iEventLink(event_id, imported.id).then(() => {
                            this.dismiss(true);
                        }, uiToast)
                    }
                })
            ])
        ]));
    }
}

class LinkOrderDialog extends UIDialog {
    constructor(order: IOrder, events: CEventAdminView[]) {
        super({ close: true, classes: 'm-500' });

        const event_map = Object.fromEntries(events.map(e => [e.id, e]));
        const event_values = events.map(e => ({ key: e.id, name: yGetI18N(e.name) })).sort((a, b) => $fn.ICOMPARE(a.name, b.name));

        const event = new UISelect(event_values, { searchable: true });
        const session = new UISelect<YId>();
        session.disabled = true;

        $on(event, 'value', e_id => {
            if(e_id) {
                const e = event_map[e_id];
                session.values = e.sessions.map(s => ({ key: s.id, name: $time(s.time).format("yyyy-MM-dd HH:mm") }))
                session.disabled = false;

                if(order.session_time) {
                    const time = $time(order.session_time);
                    const matching = e.sessions.find(s => $time(s.time).equal(time));
                    if(matching)
                        session.value = matching.id;
                }

            } else {
                session.values = [];
                session.disabled = true;
            }
        });

        if(order.event_name) {
            const name = order.event_name.toLocaleLowerCase();
            for(const v of event_values) {
                if(v.name.toLocaleLowerCase() == name)
                    event.value = v.key;
            }
        }

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('subtitle', 'Link event'),
            order.event_name && $el.H3('heading', order.event_name),
            order.session_time && $el.H4('subheading', $time(order.session_time).format("yyyy-MM-dd HH:mm")),
            new UILabeled(event, { above: 'Live event' }),
            new UILabeled(session, { above: 'Session' }),
            $el.DIV('l-button-row', [
                new UIButton({ title: "Cancel", classes: 'm-round m-compact', on_click: () => { this.dismiss(null) } }),
                new UIButton({
                    title: "Link", classes: 'm-round m-compact m-hollow', on_click: () => {
                        const session_id = session.value;
                        if(!session_id) return;

                        return iOrderLink(order.id, session_id).then(() => {
                            this.dismiss(true);
                        }, uiToast)
                    }
                })
            ])
        ]));
    }
}

class EventScreen extends UIScreen {
    constructor(private _event: IEventAdminView) {
        super();
    }

    protected override fillContent(content: UIViewSpec): void {
        const event = this._event;

        content.classList.add('tl-admin-page');

        this.title = yGetI18N(event.name);

        const form_container = $el.FORM('ui-section w-form l-form');

        const sessions_table = $el.TABLE(['Time', 'Tickets available'], event.sessions.map(s => [$time(s.time).format("yyyy-MM-dd HH:mm"), 0 + '']), // TODO
            'tl-frame-table');
        const raw_keys = Object.keys(event.provider_data).sort((a, b) => a.localeCompare(b));
        const raw_table = $el.TABLE(['Key', 'Value'], raw_keys.map(k => [k, event.provider_data[k] + '']), 'tl-frame-table')

        form_container.ui_add([
            new UILabeled($el.B(yGetI18N(event.name)), { above: 'Event name' }),
            new UILabeled($el.B(c_producers.maybe(event.producer_id)?.name || '-'), { above: 'Producer' }),
            new UILabeled($el.B(c_venues.maybe(event.venue_id!)?.name || '-'), { above: 'Venue' }),
            new UILabeled($el.B(yGetI18N(event.description)), { above: 'Event description' }),
            // new UILabeled($el.B(event.length + ' minutes'), { above: 'Length' }),
            // new UILabeled($el.B(event.regular_price/100 + ' SEK'), { above: 'Regular price' }),
            new UILabeled(sessions_table, { above: 'Sessions' }),
            new UILabeled(raw_table, { above: 'Raw fields' }),
        ]);

        // if(event.model.event) {
        //     cEventAdminViewGet(event.model.event).then(e => {
        //         form_container.ui_add($el.DIV('l-input-row', [
        //             'Linked to\u00a0',
        //             new UIButton({ kind: 'k-text', classes: 'm-inline', title: e.name, on_click: () => {
        //                 $location.push('event', { event_id: $id.objectIdToId(e.model.id) });
        //             }})
        //         ]));
        //     });
        // } else {
        form_container.ui_add([
            new UIButton({
                kind: 'k-text', title: 'Create event', on_click: () => {
                    $location.push('event', { imported_id: event.id });
                }
            }),
            new UIButton({
                kind: 'k-text', title: 'Link to existing event', on_click: () => cEventsProducerView(this._event.producer_id).then(events => {
                    new LinkEventDialog(event, events).present();
                })
            })
        ]);
        // }

        content.ui_add(form_container);
    }
}

export class AdminImportedEventsScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        let all_events = await i_events.getAll();

        const doSearch = () => {
            let events = all_events;

            events = filter_bar.search_input!.filterArray(events, (e, w) => {
                if(yGetI18N(e.name).toLowerCase().includes(w)) return true;
                if(c_producers.maybe(e.producer_id)?.name.toLowerCase().includes(w)) return true;
                return false;
            });

            count.number = events.length;
            source.data = events;
        }

        const doRefresh = async () => {
            all_events = await i_events.getAll(true)
            doSearch();
        };

        const count = new ABObjectCount('events');
        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            on_refresh: doRefresh,
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<IEventAdminView>();
        def.addValue(e => yGetI18N(e.name), { title: 'Name', width: 200 });
        def.addValue(e => c_producers.maybe(e.producer_id)?.name || e.producer_id as string, { title: 'Producer', width: 100 });
        def.addValue(e => iEventGetNextOrFirstTime(e), $fmt.DATE('d MMM yyyy HH:mm'), { title: 'Next session', width: -150 });
        // def.addValue(e => $id.isValidObjectId(e.model.event), $fmt.CHECKMARK, { title: 'Linked', width: -80 });

        def.addButtons([
            // { icon: UIIcon.PENCIL, tooltip: 'View', action: item => {
            //     $location.push('event', { event_id: $id.objectIdToId(item.model.event) });
            // }, when: i => $id.isValidObjectId(i.model.event) },
            {
                icon: UIIcon.EYE, tooltip: 'View', action: item => {
                    $location.push({ transient: new EventScreen(item) });
                }
            },
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Remove', action: item => {
                    return showWarning({ text: "Are you sure you want to delete this event?" }).then(() => {
                        return i_events.remove(item.id).then(() => {
                            source.remove(item);
                        }, uiToast);
                    });
                }
            }
        ], { width: -62 });
        def.preference_prefix = 'imported_events';

        const source = new YArrayDataSource<IEventAdminView>

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ]))
        ]);

        doSearch();
        this.onViewDidReappear = doSearch;
    }
}

class ProviderScreen extends UIScreen {
    constructor(private _provider?: IProvider) {
        super();
        this.title = _provider?.engine || 'New provider';
    }

    protected override fillContent(content: UIViewSpec): void {
        const provider = this._provider;

        const producer_id = new ABProducerSelect({ required: true });
        const engine = new UITextInput({ required: true });
        const connection = new UITextInput({ required: true });
        const is_active = new UICheckbox({ title: 'Is active' });

        const form = $form({ producer_id, engine, connection, is_active });

        if(provider)
            form.value = provider;

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(producer_id, { above: 'Producer' }),
            new UILabeled(engine, { above: 'Slug' }),
            new UILabeled(connection, { above: 'Connection string' }),
            is_active
        ]);

        content.ui_add([
            section,
            makeActionButton(UIIcon.THUMBS_UP, provider ? 'Update' : 'Create', () => {
                if(!form.validate()) return;
                const value = form.value;

                i_providers.upsert(provider?.id, value).then(() => {
                    $location.pop();
                }, uiToast);
            })
        ]);
    }
}

export class AdminProvidersScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        let all_providers = (await Promise.all([i_providers.getAll(true), c_producers.getAll()]))[0];

        const doSearch = () => {
            let providers = filter_bar.search_input!.filterArray(all_providers, (p, w) => {
                if(c_producers.maybe(p.producer_id)?.name.toLowerCase().includes(w)) return true;
                if(p.engine.toLowerCase().includes(w)) return true;
                return false;
            });

            count.number = providers.length;
            source.data = providers;
        }

        const count = new ABObjectCount('providers');
        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<IProvider>();
        def.addValue(p => c_producers.maybe(p.producer_id)?.name || '', { title: 'Producer', width: 200 });
        def.addValue(p => p.engine, { title: 'Engine', width: -200 });
        def.addValue(p => p.connection, { title: 'Connection', width: 200, classes: 't-ellipsize' });
        def.addValue(p => p.is_active, $fmt.CHECKMARK, { title: 'Active', width: -70 });

        def.addButtons([
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push({ transient: new ProviderScreen(item) });
                }
            },
        ], { width: -31 });

        def.preference_prefix = 'import_providers';

        const source = new YArrayDataSource<IProvider>

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New provider', () => { $location.push({ transient: new ProviderScreen }) })
        ]);

        doSearch();

        this.onViewDidReappear = () => {
            i_providers.getAll(true).then(providers => {
                all_providers = providers;
                doSearch();
            })
        }
    }
}

export class AdminNewOrdersScreen extends UIScreen {
    private _source!: YArrayDataSource<IOrder>;
    private _all_orders?: IOrder[];
    private _filter_bar!: ABFilterBar;
    private _count!: ABObjectCount;

    protected override async fillContent(content: UIViewSpec) {
        const def = new UITableDefinition<IOrder>();
        def.addValue(o => c_producers.maybe(i_providers.maybe(o.provider_id)!.producer_id)!.name || '', { title: 'Producer', width: 200 });
        def.addValue(o => o.event_name ?? '-', { title: 'Event Name', width: 200 });
        def.addValue(o => $time(o.session_time), $fmt.DATE('d MMM yyyy HH:mm', '-'), { title: 'Session Time', width: -120 });
        def.addValue(o => $time(o.expires_at), $fmt.DATE('d MMM yyyy HH:mm', '-'), { title: 'Expires At', width: -140 });
        def.addValue(o => o.quantity ?? 0, $fmt.NUMBER, { title: 'Quantity', width: -120 });

        def.addButtons([
            {
                icon: UIIcon.LINK, tooltip: 'Link', action: item => {
                    return cEventsAdminViewByProducer(i_providers.maybe(item.provider_id)!.producer_id).then(events => {
                        return new LinkOrderDialog(item, events).present().then(() => { this._doRefresh() })
                    });
                }
            },
        ], { width: -31 });

        def.preference_prefix = 'new_orders';

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                this._filter_bar = new ABFilterBar({
                    on_search: () => this._doSearch(),
                    right_content: [
                        this._count = new ABObjectCount('orders'),
                        new UIButton({ kind: 'k-filter', icon: UIIcon.ROTATE, on_click: () => this._doRefresh() }),
                    ],
                    no_header: true
                }),
                new UITable(def, this._source = new YArrayDataSource),
            ])),
        ]);

        this._all_orders = (await Promise.all([i_orders.getAll(true), i_providers.getAll(true), c_producers.getAll()]))[0].filter(o => !o.c_session_id);
        this._doSearch();
    }

    private _doRefresh() {
        return i_orders.getAll(true).then(orders => {
            this._all_orders = orders.filter(o => !o.c_session_id);
            this._doSearch();
        });
    }

    private _doSearch() {
        if(!this._all_orders) return;

        let orders = this._filter_bar.search_input!.filterArray(this._all_orders, (p, w) => {
            if(c_producers.maybe(i_providers.maybe(p.provider_id)!.producer_id)?.name.toLowerCase().includes(w)) return true;
            if(p.event_name?.toLowerCase().includes(w)) return true;
            return false;
        });

        this._count.number = orders.length;
        this._source.data = orders;
    }
}

export class AdminLinkedOrdersScreen extends UIScreen {
    private _source!: YArrayDataSource<[IOrder, CSessionEventView]>;
    private _all_orders?: IOrder[];
    private _all_session_map?: Record<YId, CSessionEventView>;
    private _filter_bar!: ABFilterBar;
    private _count!: ABObjectCount;
    private _show_old!: UICheckbox;

    protected override async fillContent(content: UIViewSpec) {
        const show_old = this._show_old = new UICheckbox({ title: 'Show old orders' });

        const def = new UITableDefinition<[IOrder, CSessionEventView]>();
        def.addCustom({
            width: -32, classes: 'tl-thumb-cell', decorator: (td, row) => {
                const item = row.item;
                const image = $el.DIV('r-image');
                uiSetBG(image, { media: item[1].event.picture_ids[0], variant: 'size-500' });
                td.appendChild(image);
            }
        });
        def.addValue(o => c_producers.maybe(o[1].event.producer_id)!.name || '', { title: 'Producer', width: 200 });
        def.addValue(o => yGetI18N(o[1].event.name), {
            title: 'Event Name', width: 200, classes: 'u-clickable', on_click: row => {
                $location.push('event', { event_id: row.item[1].event_id });
            }
        });
        def.addValue(o => $time(o[1].time), $fmt.DATE('d MMM yyyy HH:mm', '-'), {
            title: 'Session Time', width: -140, classes: 'u-clickable', on_click: row => {
                $location.push('session', { event_id: row.item[1].event_id, session_id: row.item[1].id });
            }
        });
        def.addValue(o => $time(o[0].expires_at), $fmt.DATE('d MMM yyyy HH:mm', '-'), { title: 'Expires At', width: -140 });
        def.addCustom({
            title: 'Bookings', width: -100, decorator: (td, row) => {
                const item = row.item;
                const booked = item[1].quantity_booked;
                const reserved = item[1].quantity_reserved;
                const total = item[1].quantity;

                if(booked != reserved) {
                    const span = $el.SPAN('r-has-tooltip', reserved + '');
                    addTooltip(booked + ' booked + ' + (reserved - booked) + ' reservations', row,
                        { anchor: span, hover: td, position: UIRelPos.Above, offset: 5 });
                    td.ui_add([span, ' / ' + total]);
                } else {
                    td.ui_add(reserved + ' / ' + total);
                }
            }, comparator: (a, b, o) => o * (a[1].quantity_booked - b[1].quantity_booked), optional: true
        });
        def.addValue(o => o[0].is_completed, $fmt.CHECKMARK, { title: 'Fulfilled', width: -90 });

        def.preference_prefix = 'linked_orders';

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-wide', [
                this._filter_bar = new ABFilterBar({
                    on_search: () => this._doSearch(),
                    right_content: [show_old, this._count = new ABObjectCount('orders')],
                    no_header: true
                }),
                new UITable(def, this._source = new YArrayDataSource),
            ])),
        ]);

        const [orders, sessions, _providers, _producers] = await Promise.all([i_orders.getAll(true), cSessionsGetManaged(), i_providers.getAll(true), c_producers.getAll()])

        this._all_orders = orders.filter(o => !!o.c_session_id);
        this._all_session_map = Object.fromEntries(sessions.map(s => [s.id, s]));
        this._doSearch();

        $on(this._show_old, 'value', () => {
            this._doSearch();
        });
    }

    private _doSearch() {
        if(!this._all_orders || !this._all_session_map) return;

        let orders = this._all_orders;

        if(!this._show_old.value) {
            const now = $time.now();
            orders = orders.filter(o => {
                return $time(o.session_time)?.after(now);
            });
        }

        this._filter_bar.search_input!.filterArray(this._all_orders, (p, w) => {
            if(c_producers.maybe(i_providers.maybe(p.provider_id)!.producer_id)?.name.toLowerCase().includes(w)) return true;
            if(p.event_name?.toLowerCase().includes(w)) return true;
            return false;
        });

        this._count.number = orders.length;
        this._source.data = orders.map(o => [o, this._all_session_map![o.c_session_id!]]);
    }
}
